import React from 'react';
import { Link } from 'react-router-dom';
import useLang from '../../i18n/useLang';
import Layout from './Layout';
import { CtaBanner } from './PageParts';

// Some translated list items still carry a typed bullet ("• ", "◦ ").
// .article draws its own list markers, so drop the typed one.
export function stripBullet(text) {
  return typeof text === 'string' ? text.replace(/^[\s•◦▪‣]+/, '') : text;
}

// "Label: body text" -> <strong>Label:</strong> body text
export function Labeled({ text }) {
  const clean = stripBullet(text);
  if (typeof clean !== 'string') return clean;
  const colon = clean.indexOf(':');
  if (colon < 1 || colon > 60) return clean;
  return (
    <>
      <strong>{clean.slice(0, colon + 1)}</strong>
      {clean.slice(colon + 1)}
    </>
  );
}

// Points toward the start edge. Direction comes from a prop rather than the rtl: variant,
// because a post pinned to dir="ltr" can sit inside <html dir="rtl">.
function BackArrow({ rtl }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 transition-transform duration-300 ease-[cubic-bezier(.16,1,.3,1)] ${
        rtl ? '-scale-x-100 group-hover:translate-x-0.5' : 'group-hover:-translate-x-0.5'
      }`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5" />
      <path d="M11 6l-6 6 6 6" />
    </svg>
  );
}

function formatDate(iso, lang) {
  if (!iso) return '';
  // Parse as a calendar date so the day never shifts with the viewer's time zone.
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}

const fits = {
  // Photos and illustrations fill the frame.
  cover: 'object-cover',
  // Diagrams and wide banners are shown whole on a white field.
  contain: 'object-contain p-4 sm:p-8',
};

/**
 * Blog post shell.
 * `dir` / `lang` pin the article to one language when its copy is not translated
 * (e.g. a Hebrew-only post stays right-to-left on the English site).
 */
export default function ArticleLayout({
  title,
  lead,
  image,
  imageAlt = '',
  imageFit = 'cover',
  imagePosition = 'object-center',
  author,
  date,
  dir,
  lang: contentLang,
  children,
}) {
  const { t, lang } = useLang();
  // The back link and the byline are site chrome: they follow the reader's language
  // even when the article itself is pinned to another direction.
  const siteDir = lang === 'he' ? 'rtl' : 'ltr';

  return (
    <Layout title={title}>
      <header className="relative overflow-hidden bg-mint-50">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 end-[-10rem] h-[28rem] w-[28rem] rounded-full bg-white/70 blur-3xl"
        />
        <div className={`wrap relative pt-10 sm:pt-14 ${image ? 'pb-28 sm:pb-40' : 'pb-16 sm:pb-20'}`}>
          <div className="mx-auto max-w-[46rem]" dir={dir}>
            <Link
              to="/Blog"
              dir={siteDir}
              className="group -ms-1 inline-flex min-h-[44px] items-center gap-2 rounded-full px-1 text-[0.95rem] font-medium text-ink-600 transition-colors hover:text-ink"
            >
              <BackArrow rtl={siteDir === 'rtl'} />
              {t('pages.blog.back')}
            </Link>
            <h1 lang={contentLang} className="display mt-6 max-w-[20ch] text-4xl text-ink sm:text-5xl lg:text-6xl">
              {title}
            </h1>
            {lead && (
              <p lang={contentLang} className="lead mt-5 max-w-[56ch]">
                {lead}
              </p>
            )}
            {(author || date) && (
              <p className="mt-7 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[0.95rem] tabular-nums text-ink-500">
                {author && (
                  <span dir={siteDir} className="font-medium text-ink-600">
                    {author}
                  </span>
                )}
                {author && date && <span aria-hidden="true">·</span>}
                {date && (
                  <time dir={siteDir} dateTime={date}>
                    {formatDate(date, lang)}
                  </time>
                )}
              </p>
            )}
          </div>
        </div>
      </header>

      {image && (
        <div className="wrap relative -mt-20 sm:-mt-28">
          <figure className="mx-auto max-w-5xl overflow-hidden rounded-[2rem] border border-fog-200 bg-white shadow-card">
            <img
              src={image}
              alt={imageAlt}
              className={`aspect-[16/9] w-full sm:aspect-[2/1] ${fits[imageFit] || fits.cover} ${imagePosition}`}
            />
          </figure>
        </div>
      )}

      <div className="wrap pt-12 sm:pt-16">
        <article dir={dir} lang={contentLang} className="article mx-auto max-w-[46rem]">
          {children}
        </article>
      </div>

      <CtaBanner />
    </Layout>
  );
}
